import React, { ReactElement } from "react"
import {
	AlertColor,
	Box,
	Button,
	Checkbox,
	Grid,
	MenuItem,
	Select,
	Typography,
} from "@mui/material"
import {
	t,
	Trans,
} from "@lingui/macro"
import {
	mdiEmoticonExcited,
	mdiEmoticonNeutral,
	mdiEmoticonSad,
	mdiPlusBox,
} from "@mdi/js"
import Icon from "@mdi/react"
import {
	Asset,
	AssetMatchBrand,
    Brand,
} from "models"
import { lightTheme } from "commons/themes"
import ProposalCreationDialog from "components/match-proposal/match-proposal-creation/proposal-creation-dialog"
import SendNewProposal from "components/match-proposal/match-proposal-creation/send-proposal/send-new-proposal"
import { AssetService } from "services/asset.service"
import { MatesComponent, MatesComponentState } from "components/mates-component"
import withUseRouter, { UseRouterParams } from "services/router"
import { ErrorResponse, RestResponse } from "@sinossi/mates-react-library"
import { SendNewProposalRequest } from "components/match-proposal/model"
import { MatchService } from "services/match.service"
import { SbSnackbar } from "components/commons/sb-snackbar"

interface CreateNewProposalsProps extends UseRouterParams{
	notVisibleMatches: AssetMatchBrand[]
	refreshNotVisible: () => void
	refreshVisible: () => void
	assetId: string
}

interface CreateNewProposalsState extends MatesComponentState {
	asset: Asset | null
	selectAll: boolean
	selectList: boolean[]
	selectedBrandList: Brand[]
	openCreation: boolean
	openSend: boolean
	openPrecompiled: boolean
	snackbarData: {
		openNotification: boolean
		severity: "info" | "success" | "error" | "warning"
		notificationMessage: string
	}
}

class CreateNewProposals extends React.Component<CreateNewProposalsProps, CreateNewProposalsState> {
	private readonly matesComponent: MatesComponent<CreateNewProposalsProps, CreateNewProposalsState> = new MatesComponent<CreateNewProposalsProps, CreateNewProposalsState>(this)

	constructor(props: CreateNewProposalsProps)
	{
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			asset: null,
			selectAll: false,
			selectList: this.props.notVisibleMatches.map(() => false),
			selectedBrandList: [],
			openCreation: false,
			openSend: false,
			openPrecompiled: false,
			snackbarData: {
				openNotification: false,
				severity: "info",
				notificationMessage: ""
			}
		}
	}

	componentDidMount() {
		AssetService.getAsset(this.props.assetId, this.getAssetCallback, this.matesComponent.onError)
	}

	private getAssetCallback = (response: RestResponse<Asset, ErrorResponse>) => {
		if(response.hasError()){
			return
		}

		this.setState(() => ({asset: response.data}))
	}

	componentDidUpdate(prevProps: CreateNewProposalsProps, prevState: CreateNewProposalsState){
		if(this.props.notVisibleMatches.length !==  prevProps.notVisibleMatches.length){
			this.setState(() => ({
				selectList: this.props.notVisibleMatches.map(() => false)
			}))
		}

		if(this.state.selectAll !== prevState.selectAll){
			let selectList: boolean[] = []
			let selectedBrandList: Brand[] = []

			if(!this.state.selectAll && this.state.selectList.some(select => !select)){
				selectList = [...this.state.selectList]
			}
			else {
				selectList = this.props.notVisibleMatches.map(() => this.state.selectAll)
			}

			selectList.forEach((selected, index) => {
				if(selected) {
					selectedBrandList.push(this.props.notVisibleMatches[index].brand)
				}
			})

			this.setState(() => ({selectList, selectedBrandList}))
		}

		if(JSON.stringify(this.state.selectList) !== JSON.stringify(prevState.selectList)){
			let selectedBrandList: Brand[] = []
			const selectAll: boolean = !this.state.selectList.some(select => !select)

			this.state.selectList.forEach((selected, index) => {
				if(selected) {
					selectedBrandList.push(this.props.notVisibleMatches[index].brand)
				}
			})

			this.setState(() => ({selectedBrandList, selectAll}))
		}
	}

	private getCompatibility = (compatibility: number): ReactElement => {
		let emoticon: ReactElement = (
			<Box sx={{display: "flex", gap: 1}}>
				<Icon path={mdiEmoticonExcited} size={1} color={lightTheme.palette.info.main} />
				<Icon path={mdiEmoticonNeutral} size={1} style={{color: "#adb5bd"}} />
				<Icon path={mdiEmoticonSad} size={1} style={{color: "#adb5bd"}} />
			</Box>
		)

		if(compatibility <= 66) {
			emoticon = (
				<Box sx={{display: "flex", gap: 1}}>
					<Icon path={mdiEmoticonExcited} size={1} style={{color: "#adb5bd"}} />
					<Icon path={mdiEmoticonNeutral} size={1} color={lightTheme.palette.info.main} />
					<Icon path={mdiEmoticonSad} size={1} style={{color: "#adb5bd"}} />
				</Box>
			)
		}

		if(compatibility <= 33) {
			emoticon = (
				<Box sx={{display: "flex", gap: 1}}>
					<Icon path={mdiEmoticonExcited} size={1} style={{color: "#adb5bd"}} />
					<Icon path={mdiEmoticonNeutral} size={1} style={{color: "#adb5bd"}} />
					<Icon path={mdiEmoticonSad} size={1} color={lightTheme.palette.info.main} />
				</Box>
			)
		}

		return emoticon
	}

	private sendNewProposal = (request: SendNewProposalRequest) => {
		this.setState(() => ({
			loading: true
		}))
		MatchService.sendNewProposal(request, this.sendNewProposalCallback, this.matesComponent.onError)
	}

	private sendNewProposalCallback = (response: RestResponse<any, ErrorResponse>) => {
		if(response.hasError()){
			this.matesComponent.onError(response)
			this.openSnackbar(
				t({id: "cnp.send-new.error", message: "Qualcosa è andato storto durante l'invio della proposta"}),
				"error"
			)
			this.setState(() => ({
				loading: false,
				openSend: false
			}))
		}

		this.openSnackbar(
			t({id: "cnp.send-new.success", message: "Proposta inviata con successo"}),
			"success"
		)
		this.setState(() => ({
			loading: false,
			openSend: false
		}), this.props.refreshVisible)
	}

	private openSnackbar = (message: string, severity: AlertColor) => {
		this.setState(() => ({
			snackbarData: {
				openNotification: true,
				notificationMessage: message,
				severity: severity
			}
		}))
	}

	render () {
		return (
			<Box
				sx={{
					width: { xs: "100%", lg: "48%" },
					height: { xs: "60vh", lg: "70vh" },
					backgroundColor: "background.paper",
					borderRadius: "10px",
					padding: { xs: 2, sm: 3 }
				}}
			>
				<Typography variant="h6" color={"text"} sx={{ fontWeight: "600" }}>
					<Trans id={"mpc.send-new-match.title"}>
						Invia nuove proposte per questo immobile
					</Trans>
				</Typography>
				<Box
					sx={{
						width: "100%",
						display: "flex",
						justifyContent: "right",
						gap: 2,
						mt: 2
					}}
				>
					<Select
						variant={"outlined"}
						placeholder={t({
							id: "cnp-filter",
							message: "Seleziona un filtro",
						})}
						name="filter"
						size="small"
						sx={{width: { xs: "50%", sm: "40%", md: "30%" }}}
						defaultValue={"Tutti"}
					>
						{
							["Tutti", "Compatibilità alta", "Da verificare", "Sconsigliato", "Solo selezionate"].map((elem, index) => (
									<MenuItem key={index} value={elem}>{elem}</MenuItem>
								)
							)
						}
					</Select>
					<Button
						variant={"contained"}
						color={"info"}
						size="small"
						sx={{
							px: 1,
							fontSize: "0.8rem"
						}}
						startIcon={<Icon path={mdiPlusBox} size={1} />}
						onClick={() => this.setState(() => ({openCreation: true}))}
					>
						<Trans id={"cnp.add-new"}>Aggiungi nuovo</Trans>
					</Button>
				</Box>
				<Grid container columnSpacing={2} sx={{mt: 3, px: 1, display: { xs: "none", md: "flex" }}} alignItems={"center"}>
					<Grid item md={3}>
						<Typography sx={{ fontWeight: "500" }}>
							<Trans id={"vsp.compatibility"}>
								Compatibilità
							</Trans>
						</Typography>
					</Grid>
					<Grid item md={5.5}>
						<Typography sx={{ fontWeight: "500" }}>
							<Trans id={"vsp.brand-name"}>
								Brand
							</Trans>
						</Typography>
					</Grid>
					<Grid item md={3.5}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								gap: 1
							}}
						>
							<Typography sx={{ fontWeight: "500", whiteSpace: "nowrap" }}>
								<Trans id={"cnp.select-all"}>
									Seleziona tutti
								</Trans>
							</Typography>
							<Checkbox
								size="small"
								checked={this.state.selectAll}
								onChange={() => {
									this.setState((prevState) => ({
										selectAll: !prevState.selectAll,
									}))
								}}
							/>
						</Box>
					</Grid>
				</Grid>
				
				{/* Mobile header */}
				<Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "space-between", alignItems: "center", mt: 2, px: 1 }}>
					<Typography variant="body2" sx={{ fontWeight: "500", whiteSpace: "nowrap" }}>
						<Trans id={"cnp.select-all"}>Seleziona tutti</Trans>
					</Typography>
					<Checkbox
						size="small"
						checked={this.state.selectAll}
						onChange={() => {
							this.setState((prevState) => ({
								selectAll: !prevState.selectAll,
							}))
						}}
					/>
				</Box>
				<Box sx={{mt: 2, backgroundColor: "grey.50", height: { xs: "38vh", lg: "42vh" }, overflowX: "hidden", overflowY: "auto", borderRadius: 1}}>
					<Grid container columnSpacing={2} rowSpacing={2} sx={{p: 2}} alignItems={"center"}>
						{
							this.props.notVisibleMatches.map((match, index) => (
								<React.Fragment key={`match-${match.brand.id}-${index}`}>
									<Grid item xs={12} md={3} sx={{ mb: { xs: 1, md: 0 } }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography variant="body2" sx={{ display: { xs: "inline", md: "none" }, fontWeight: 500 }}>
												Compatibilità:
											</Typography>
											{this.getCompatibility(match.compatibility || 0)}
										</Box>
									</Grid>
									<Grid item xs={12} md={5.5} sx={{ mb: { xs: 1, md: 0 } }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography variant="body2" sx={{ display: { xs: "inline", md: "none" }, fontWeight: 500 }}>
												Brand:
											</Typography>
											<Typography sx={{ fontWeight: "400", fontSize: "0.9rem" }}>
												{match.brand.name}
											</Typography>
										</Box>
									</Grid>
									<Grid item xs={12} md={3.5}>
										<Box
											sx={{
												width: "100%",
												display: "flex",
												alignItems: "center",
												justifyContent: { xs: "space-between", md: "flex-end" },
												gap: 1
											}}
										>
											<Typography variant="body2" sx={{ display: { xs: "inline", md: "none" }, fontWeight: 500 }}>
												Seleziona:
											</Typography>
											<Checkbox
												size="small"
												checked={this.state.selectList[index] || false}
												onChange={() => {
													let newList = [...this.state.selectList]
													newList[index] = !newList[index]
													this.setState(() => ({
														selectList: newList
													}))
												}}
											/>
										</Box>
									</Grid>
								</React.Fragment>
							))
						}
					</Grid>
				</Box>
				<Box
					sx={{
						width: "100%",
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						justifyContent: "flex-end",
						mt: 2,
						gap: 1.5,
						px: 1
					}}
				>
					<Button
						variant={"contained"}
						color={"info"}
						size="small"
						sx={{
							px: 1.5,
							fontSize: "0.75rem",
							width: { xs: "100%", sm: "auto" },
							minWidth: { sm: "140px" },
							whiteSpace: "nowrap"
						}}
						onClick={() => this.setState(() => ({openSend: true, openPrecompiled: false}))}
					>
						<Trans id={"cnp.send-new"}>Scrivi proposta</Trans>
					</Button>
					<Button
						variant={"contained"}
						color={"info"}
						size="small"
						sx={{
							px: 1.5,
							fontSize: "0.75rem",
							width: { xs: "100%", sm: "auto" },
							minWidth: { sm: "160px" },
							whiteSpace: "nowrap"
						}}
						onClick={() => this.setState(() => ({openSend: true, openPrecompiled: true}))}
					>
						<Trans id={"cnp.send-new-with-template"}>Usa proposta predefinita</Trans>
					</Button>
				</Box>
				{
					this.state.openCreation &&
					<ProposalCreationDialog
						onClose={() => this.setState(() => ({openCreation: false}))}
						closeWithRefresh={() => {
							this.setState(() => ({openCreation: false}), this.props.refreshNotVisible)
						}}
						assetId={this.props.assetId}
					/>
				}
				{
					this.state.asset && this.state.openSend &&
					<SendNewProposal
						asset={this.state.asset}
						onClose={() => this.setState(() => ({openSend: false}))}
						submit={this.sendNewProposal}
						selectedBrands={this.state.selectedBrandList}
						allBrands={this.props.notVisibleMatches.map(match => match.brand)}
						precompiled={this.state.openPrecompiled}
					/>
				}
				<SbSnackbar
					snackbarData={this.state.snackbarData}
					handleCloseNotification={() => {
						this.setState((prevState) => ({
							snackbarData: {...prevState.snackbarData, openNotification: false}
						}))
					}} />
			</Box>
		)
	}
}

export default withUseRouter(CreateNewProposals)
